/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState} from 'react'
import {Link} from 'react-router-dom'
import PropTypes from 'prop-types'
import {Skeleton} from '@chakra-ui/react'
import {isHydrated} from '../../utils/utils'
import DynamicImage from '@app/components/dynamic-image'

/**
 * PDCImage is a React functional component that renders an optimized, responsive image with optional skeleton loading and link wrapping.
 *
 * This component manages image loading state to prevent Cumulative Layout Shift (CLS) by displaying a skeleton placeholder until the image is fully loaded.
 * It supports dynamic image sources, custom image properties, and can wrap the image in a link if a URL is provided. The component also handles accessibility
 * attributes such as `alt` and `title`, and adjusts image loading priority for improved performance.
 *
 * Props:
 * @param {string|object} currentImage - The image source URL or an object containing image data.
 * @param {number[]} imageWidths - An array of widths for responsive image rendering.
 * @param {string} imageAlt - The alt text for the image. If set to 'empty', an empty string is used.
 * @param {string} imageTitle - The title attribute for the image. If set to 'empty', an empty string is used.
 * @param {object} imageProps - Additional props to pass to the image element.
 * @param {string} url - Optional. If provided, wraps the image in a link pointing to this URL.
 * @param {boolean} displayPriority - If true, the image loads eagerly with high fetch priority and displays a skeleton until loaded.
 * @param {object} skeletonProps - Props to customize the skeleton placeholder.
 * @param {string} title - Fallback title for the image if `imageTitle` is not provided.
 * @param {...object} props - Additional props passed to the underlying DynamicImage component.
 *
 * @returns {JSX.Element} A responsive image with skeleton loading and optional link wrapping, or a skeleton if the image is not available or not hydrated.
 */
const PDCImage = ({
    currentImage,
    imageWidths,
    imageAlt,
    imageTitle,
    imageProps,
    url,
    displayPriority,
    skeletonProps,
    title,
    ...props
}) => {
    const [isImageLoaded, setIsImageLoaded] = useState(false)
    const defaultImageProps = {
        width: '100%',
        loading: displayPriority ? 'eager' : 'lazy',
        fetchpriority: displayPriority ? 'high' : null,
        alt: imageAlt === 'empty' ? '' : imageAlt || title,
        title: imageTitle === 'empty' ? '' : imageTitle || title,
        onLoad: () => setIsImageLoaded(true),
        style: {display: isImageLoaded || !displayPriority ? 'block' : 'none'}
    }

    const mergedImageProps = {...defaultImageProps, ...imageProps}
    const Wrapper = url ? ImageWrapperLink : React.Fragment

    return isHydrated() && currentImage ? (
        <>
            {
                // Prevent CLS while image is loading
                !isImageLoaded && displayPriority && <Skeleton {...skeletonProps} />
            }
            <Wrapper {...(url ? {url: url} : {})}>
                <DynamicImage
                    src={`${
                        typeof currentImage === 'string' ? currentImage : currentImage.url
                    }[?sw={width}]`} // Default image for server-side rendering
                    widths={imageWidths}
                    imageProps={mergedImageProps}
                    {...props}
                />
            </Wrapper>
        </>
    ) : (
        <Skeleton {...skeletonProps} />
    )
}

PDCImage.propTypes = {
    currentImage: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    imageWidths: PropTypes.array,
    imageAlt: PropTypes.string,
    imageTitle: PropTypes.string,
    imageProps: PropTypes.object,
    url: PropTypes.string,
    displayPriority: PropTypes.bool,
    skeletonProps: PropTypes.object,
    title: PropTypes.string
}

export default PDCImage

/**
 * Conditionally wraps its children in a React Router <Link> component if a URL is provided.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The elements to be potentially wrapped by the link.
 * @param {string} [props.url] - The URL to link to. If not provided, children are rendered as-is.
 * @returns {React.ReactNode} The children wrapped in a <Link> if a URL is present, otherwise the children themselves.
 */
export const ImageWrapperLink = ({children, url}) => {
    return url ? <Link to={url}>{children}</Link> : children
}

ImageWrapperLink.propTypes = {
    children: PropTypes.node,
    url: PropTypes.string
}
